import {
  Button,
  Icon,
  InputCheckbox,
  ModalBody
} from "@silversea-ssc/ui-components-react";
import React, { FC } from "react";
import { FormProvider } from "react-hook-form";
import * as styles from "./SubscribeCrossBrandOptions.module.scss";
import { Trans } from "@lingui/react/macro";
import { useCrossBrandLeadForm } from "components/DefaultSubscribeForm/useCrossBrandLeadForm";
import {
  CrossBrandLeadFormData,
  LeadFormData
} from "components/DefaultSubscribeForm/types";
import AsyncForm from "components/Form/AsyncForm";
import clsx from "clsx";
import { INavLink } from "components/HeaderNavBar/type";

export const SubscribeCrossBrandOptionsModal: React.FC<
  React.PropsWithChildren<Omit<SubscribeCrossBrandOptionsProps, "variant">>
> = ({ onSubmitSuccess, leadData, privacyPolicyUrl }) => {
  return (
    <ModalBody variant="boxed" className={styles.modal}>
      <SubscribeCrossBrandOptions
        onSubmitSuccess={onSubmitSuccess}
        leadData={leadData}
        variant="wide"
        privacyPolicyUrl={privacyPolicyUrl}
      />
    </ModalBody>
  );
};

export type LeadData = {
  leadFormData: LeadFormData;
  leadApiResponse: any;
};

interface SubscribeCrossBrandOptionsProps {
  onSubmitSuccess: (formData: CrossBrandLeadFormData) => void;
  leadData: LeadData;
  variant: "compact" | "wide";
  privacyPolicyUrl: INavLink;
}

const SubscribeCrossBrandOptions: FC<
  React.PropsWithChildren<SubscribeCrossBrandOptionsProps>
> = ({ onSubmitSuccess, leadData, variant, privacyPolicyUrl }) => {
  return (
    <div
      className={clsx(styles.body, {
        [styles.variantCompact]: variant == "compact",
        [styles.variantWide]: variant == "wide"
      })}
    >
      <div className={styles.subscriptionConfirmedMessage}>
        <Icon name="circle-check" size="1x" className={styles.icon} />
        <span>
          <Trans>Subscription confirmed</Trans>
        </span>
      </div>
      <div className={styles.content}>
        <SubscribeCrossBrandOptionsContent />
        <ActualCrossBrandOptionsForm
          onSubmitSuccess={onSubmitSuccess}
          leadData={leadData}
          privacyPolicyUrl={privacyPolicyUrl}
        />
      </div>
    </div>
  );
};

export default SubscribeCrossBrandOptions;

const SubscribeCrossBrandOptionsContent: React.FC = () => {
  return (
    <div className={styles.additionalCheckboxesMessageContainer}>
      <div className={styles.additionalCheckboxesTitle}>
        <Trans>Thank you for signing up</Trans>
      </div>
      <div className={styles.additionalCheckboxesMessage}>
        <Trans>
          We invite you to sign up to receive emails from our family of brands.
        </Trans>
      </div>
    </div>
  );
};

type SubscribeCrossBrandOptionsFooterProps = {
  privacyPolicyUrl: INavLink;
};

const SubscribeCrossBrandOptionsFooter: React.FC<
  SubscribeCrossBrandOptionsFooterProps
> = ({ privacyPolicyUrl }) => {
  return (
    <div className={styles.disclaimer}>
      <Trans>
        You can unsubscribe at any time. For more details about how we use your
        information, view our
      </Trans>{" "}
      <a
        className={styles.privacyPolicyLink}
        target="_blank"
        rel="noopener noreferrer"
        href={privacyPolicyUrl.href}
      >
        {privacyPolicyUrl.title}
      </a>
      .
    </div>
  );
};

const validateAtLeastOneChecked = (
  value: boolean,
  allValues: CrossBrandLeadFormData
) => value || allValues.RoyalCaribbean || allValues.CelebrityCruises;

type ActualCrossBrandOptionsFormProps = {
  onSubmitSuccess: (formData: CrossBrandLeadFormData) => void;
  leadData: LeadData;
  privacyPolicyUrl: INavLink;
};

const ActualCrossBrandOptionsForm: FC<ActualCrossBrandOptionsFormProps> = ({
  onSubmitSuccess,
  leadData,
  privacyPolicyUrl
}) => {
  const formName = "cross-brand-lead-form";
  const { formMethods, onSubmit } = useCrossBrandLeadForm({
    onSubmitSuccess,
    leadData,
    formName
  });

  const { formState, register, getValues } = formMethods;

  return (
    <FormProvider {...formMethods}>
      <AsyncForm name={formName} onSubmit={onSubmit} className={styles.form}>
        <div className={styles.fields}>
          <InputCheckbox
            {...register("RoyalCaribbean", {
              validate: (value) => validateAtLeastOneChecked(value, getValues())
            })}
            id="subscribe-modal-form-royal-caribbean"
            name="RoyalCaribbean"
          >
            Royal Caribbean International
          </InputCheckbox>
          <InputCheckbox
            {...register("CelebrityCruises", {
              validate: (value) => validateAtLeastOneChecked(value, getValues())
            })}
            id="subscribe-modal-form-celebrity-cruises"
            name="CelebrityCruises"
          >
            Celebrity Cruises
          </InputCheckbox>
        </div>

        <SubscribeCrossBrandOptionsFooter privacyPolicyUrl={privacyPolicyUrl} />

        <Button
          className={styles.bodyFooterButton}
          renderAs="button"
          type="submit"
          size="regular"
          loading={formState.isSubmitting}
          disabled={!formState.isValid}
          data-testid="submit"
          dataContentPiece="submit_button"
        >
          <Trans>CONFIRM</Trans>
        </Button>
      </AsyncForm>
    </FormProvider>
  );
};
